import type { FC, ReactNode } from 'react'
import type { ActivityBucket } from '@/types'

import { useMemo } from 'react'
import { cn } from '@/lib/utils'
import { Skeleton } from '@/components/ui/skeleton'
import { useMounted } from '@/hooks/use-mounted'
import { useSettings } from '@/components/generals/settings-provider'
import { ACTIVITY_DAYS } from '@/constants/inventory'

interface ActivityChartProps {
    activity: ActivityBucket[]
}

type DayTotals = {
    key: string
    date: Date
    unitsIn: number
    unitsOut: number
}

const pad = (value: number): string => String(value).padStart(2, '0')

const dayKey = (date: Date): string => `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`

const buildDays = (activity: ActivityBucket[]): DayTotals[] => {
    const today = new Date()
    const days: DayTotals[] = []

    today.setHours(0, 0, 0, 0)

    for (let offset = ACTIVITY_DAYS - 1; offset >= 0; offset--) {
        const date = new Date(today)

        date.setDate(today.getDate() - offset)
        days.push({ key: dayKey(date), date, unitsIn: 0, unitsOut: 0 })
    }

    const byKey = new Map(days.map(day => [day.key, day]))

    for (const bucket of activity) {
        const moment = new Date(`${bucket.hour}:00:00Z`)

        if (Number.isNaN(moment.getTime())) continue

        const day = byKey.get(dayKey(moment))

        if (!day) continue
        if (bucket.type === 'entrada') day.unitsIn += bucket.units
        if (bucket.type === 'salida') day.unitsOut += bucket.units
    }

    return days
}

const niceMax = (value: number): number => {
    if (value <= 4) return 4

    const magnitude = 10 ** Math.floor(Math.log10(value))
    const steps = [1, 2, 2.5, 5, 10]
    const step = steps.find(candidate => candidate * magnitude >= value) ?? 10

    return step * magnitude
}

const ActivityChart: FC<ActivityChartProps> = ({ activity }): ReactNode => {
    const mounted = useMounted()

    if (!mounted) {
        return (
            <div className='flex flex-col gap-4' aria-hidden='true'>
                <Skeleton className='h-5 w-56' />
                <Skeleton className='h-48 w-full' />
            </div>
        )
    }

    return <ActivityChartContent activity={activity} />
}

const ActivityChartContent: FC<ActivityChartProps> = ({ activity }): ReactNode => {
    const { number } = useSettings()
    const days = useMemo(() => buildDays(activity), [activity])

    const dayFormat = useMemo(() => new Intl.DateTimeFormat('es-ES', { weekday: 'short', day: 'numeric', month: 'short' }), [])
    const totalIn = days.reduce((sum, day) => sum + day.unitsIn, 0)
    const totalOut = days.reduce((sum, day) => sum + day.unitsOut, 0)
    const peak = Math.max(...days.map(day => Math.max(day.unitsIn, day.unitsOut)), 0)
    const max = niceMax(peak)
    const height = (value: number) => (value > 0 ? Math.max(3, (value / max) * 100) : 0)

    return (
        <div className='flex flex-col gap-4'>
            <ul className='flex flex-wrap items-center gap-x-5 gap-y-1.5 text-sm'>
                <li className='flex items-center gap-2'>
                    <span className='size-2.5 rounded-[3px] bg-flow-in' aria-hidden='true' />
                    Entradas
                    <span className='font-semibold tabular-nums'>{number(totalIn)}</span>
                </li>

                <li className='flex items-center gap-2'>
                    <span className='size-2.5 rounded-[3px] bg-flow-out' aria-hidden='true' />
                    Salidas
                    <span className='font-semibold tabular-nums'>{number(totalOut)}</span>
                </li>

                <li className='text-xs text-muted-foreground'>
                    Unidades por día
                </li>
            </ul>

            {peak === 0 ? (
                <div className='flex h-48 items-center justify-center rounded-lg border border-dashed text-center text-sm text-muted-foreground'>
                    Sin entradas ni salidas en los últimos {ACTIVITY_DAYS} días.
                </div>
            ) : (
                <div className='flex gap-2' aria-hidden='true'>
                    <div className='flex h-48 w-8 shrink-0 flex-col justify-between pb-6 text-right text-[0.68rem] text-muted-foreground tabular-nums'>
                        <span className='-translate-y-1/2'>{number(max)}</span>
                        <span>{number(max / 2)}</span>
                        <span className='translate-y-1/2'>0</span>
                    </div>

                    <div className='relative flex-1'>
                        <div className='pointer-events-none absolute inset-x-0 top-0 bottom-6 flex flex-col justify-between'>
                            <span className='border-t border-dashed border-border' />
                            <span className='border-t border-dashed border-border' />
                            <span className='border-t border-border' />
                        </div>

                        <div className='relative flex h-48 items-stretch gap-0.5 sm:gap-1'>
                            {days.map((day, index) => {
                                const isToday = index === days.length - 1
                                const align = index < 3 ? 'left-0' : index > days.length - 4 ? 'right-0' : 'left-1/2 -translate-x-1/2'

                                return (
                                    <div key={day.key} className='group relative flex flex-1 flex-col'>
                                        <div className='flex flex-1 items-end justify-center gap-0.5 rounded-t-md pb-px group-hover:bg-muted/60'>
                                            <div
                                                className='w-full max-w-2.5 rounded-t-[4px] bg-flow-in transition-[height] duration-500'
                                                style={{ height: `${height(day.unitsIn)}%` }}
                                            />

                                            <div
                                                className='w-full max-w-2.5 rounded-t-[4px] bg-flow-out transition-[height] duration-500'
                                                style={{ height: `${height(day.unitsOut)}%` }}
                                            />
                                        </div>

                                        <span
                                            className={cn(
                                                'flex h-6 items-end justify-center text-[0.68rem] tabular-nums',
                                                isToday ? 'font-semibold text-foreground' : 'text-muted-foreground',
                                                index % 2 === 1 && !isToday && 'invisible sm:visible'
                                            )}
                                        >
                                            {day.date.getDate()}
                                        </span>

                                        <div
                                            className={cn(
                                                'pointer-events-none absolute bottom-full z-10 mb-1 hidden w-max min-w-36 rounded-lg bg-foreground px-3 py-2 text-xs text-background shadow-lg group-hover:block',
                                                align
                                            )}
                                        >
                                            <p className='mb-1 font-medium capitalize'>
                                                {isToday ? 'Hoy' : dayFormat.format(day.date)}
                                            </p>

                                            <p className='flex items-center justify-between gap-4'>
                                                <span className='flex items-center gap-1.5'>
                                                    <span className='size-2 rounded-[2px] bg-flow-in' />
                                                    Entradas
                                                </span>

                                                <span className='font-semibold tabular-nums'>{number(day.unitsIn)}</span>
                                            </p>

                                            <p className='flex items-center justify-between gap-4'>
                                                <span className='flex items-center gap-1.5'>
                                                    <span className='size-2 rounded-[2px] bg-flow-out' />
                                                    Salidas
                                                </span>

                                                <span className='font-semibold tabular-nums'>{number(day.unitsOut)}</span>
                                            </p>
                                        </div>
                                    </div>
                                )
                            })}
                        </div>
                    </div>
                </div>
            )}

            <table className='sr-only'>
                <caption>
                    Unidades que entraron y salieron cada día durante los últimos {ACTIVITY_DAYS} días
                </caption>

                <thead>
                    <tr>
                        <th scope='col'>Día</th>
                        <th scope='col'>Entradas</th>
                        <th scope='col'>Salidas</th>
                    </tr>
                </thead>

                <tbody>
                    {days.map(day => (
                        <tr key={day.key}>
                            <th scope='row'>{dayFormat.format(day.date)}</th>
                            <td>{number(day.unitsIn)}</td>
                            <td>{number(day.unitsOut)}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    )
}

export default ActivityChart
